import { useId, type ReactNode } from 'react';

import type { ExtractedField } from '@dcc-erp/shared';

export interface ConfidenceFieldProps {
  fieldKey: string;
  label: string;
  field: ExtractedField;
  /** react-hook-form dirtyFields[name] — true once the human edited it. */
  isDirty: boolean;
  /** Enter-confirmed (provenance: human-confirmed). */
  isConfirmed: boolean;
  /** Inline validation message (critical or warning) tied to this field. */
  invalidMessage?: string | null;
  /** When true the validation entry is `critical` → set aria-invalid. */
  isCritical?: boolean;
  /** The actual control (input / AmountInput / SearchSelect), aria-wired by the parent. */
  children: ReactNode;
  onReveal: (fieldKey: string) => void;
  onReset: () => void;
}

const CONF_CLASS: Record<ExtractedField['confidence'], string> = {
  high: 'conf--high',
  med: 'conf--med',
  low: 'conf--low',
};

export function ConfidenceField({
  fieldKey,
  label,
  field,
  isDirty,
  isConfirmed,
  invalidMessage,
  isCritical,
  children,
  onReveal,
  onReset,
}: ConfidenceFieldProps) {
  const hintId = useId();
  const msgId = useId();
  const conf = field.confidence;

  // Provenance: confirmed (✓) wins over edited (dot) wins over extracted.
  const provenance = isConfirmed ? (
    <span className="badge positive" aria-label="ยืนยันแล้ว" title="ยืนยันแล้ว">
      ✓
    </span>
  ) : isDirty ? (
    <span className="badge warning" title="ผู้ใช้แก้ไขแล้ว">
      <span className="bdot" aria-hidden /> แก้ไขแล้ว
    </span>
  ) : null;

  // aria wiring passed down to the single child control. critical => aria-invalid;
  // low confidence is NOT invalid — it only earns a describedby hint.
  const describedBy =
    [invalidMessage ? msgId : null, conf === 'low' ? hintId : null].filter(Boolean).join(' ') ||
    undefined;
  const control = (
    <div
      // Wrapper passes aria to the control via a data-pattern the parent reads;
      // here we just surface the ids so tests/parent can attach them. The route
      // spreads {ariaInvalid, ariaDescribedBy} from this component's render-prop
      // contract; for the controlled children we set them directly:
      ref={(el) => {
        if (!el) return;
        const input = el.querySelector('input, textarea, [role="combobox"]');
        if (!input) return;
        if (isCritical) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
        if (describedBy) input.setAttribute('aria-describedby', describedBy);
        else input.removeAttribute('aria-describedby');
      }}
    >
      {children}
    </div>
  );

  return (
    <div className={`conf-field ${CONF_CLASS[conf]}`}>
      <div className="conf-field__head">
        <span className="conf-field__label">{label}</span>
        {conf === 'med' && (
          <span className="badge warning">
            <span aria-hidden>⚠</span> ตรวจสอบ
          </span>
        )}
        {conf === 'low' && (
          <span className="badge negative">
            <span aria-hidden>⚠</span> ตรวจสอบ
          </span>
        )}
        {provenance}
        {field.page != null && (
          <button
            type="button"
            className="btn sm ghost"
            aria-label="แสดงในเอกสาร"
            title="แสดงในเอกสาร"
            onClick={() => onReveal(fieldKey)}
          >
            👁
          </button>
        )}
        {isDirty && (
          <button
            type="button"
            className="btn sm ghost"
            aria-label="คืนค่าที่อ่านได้"
            title="คืนค่าที่อ่านได้"
            onClick={onReset}
          >
            ↺
          </button>
        )}
      </div>
      {control}
      {invalidMessage && (
        <div id={msgId} className="conf-field__msg" role={isCritical ? 'alert' : undefined}>
          {invalidMessage}
        </div>
      )}
      {conf === 'low' && (
        <div id={hintId} className="conf-field__hint">
          ความเชื่อมั่นต่ำ — โปรดตรวจสอบค่าที่อ่านได้
        </div>
      )}
      {field.sourceSnippet && field.page != null && (
        <div className="source-snippet">
          “{field.sourceSnippet}” · หน้า {field.page}
        </div>
      )}
    </div>
  );
}
